import { Search, X } from "lucide-react";
import type { ReactNode } from "react";
import {
  Dropdown,
  type DropdownOption,
} from "./Dropdown";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { ViewModeToggle } from "./DataTable";
import { RowsPerCardSelect } from "./RowsPerCardSelect";
import { DEFAULT_PAGE_SIZE } from "../../utils/formatters";

export interface ToolbarFilter {
  key: string;
  value?: string;
  onChange?: (value: string) => void;
  multiple?: boolean;
  /** Multi-select only: the top row ticks every option instead of clearing. */
  selectAll?: boolean;
  values?: string[];
  onValuesChange?: (values: string[]) => void;
  options: DropdownOption[];
  placeholder?: string;
  leadingLabel?: string;
  searchable?: boolean;
  disabled?: boolean;
  className?: string;
  hidden?: boolean;
}

export interface ToolbarSearch {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

export interface TableToolbarProps {
  search?: ToolbarSearch;
  filters?: ToolbarFilter[];

  viewMode?: "card" | "table";
  onViewModeChange?: (mode: "card" | "table") => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeLabel?: string;
  /** Renders the view toggle after the page-size select instead of before it. */
  viewToggleLast?: boolean;
  actions?: ReactNode;
  trailing?: ReactNode;
  filterTrailing?: React.ReactNode;
  className?: string;
}

export function TableToolbar({
  search,
  filters,
  viewMode,
  onViewModeChange,
  pageSize,
  onPageSizeChange,
  pageSizeLabel,
  viewToggleLast,
  actions,
  filterTrailing,
  trailing,
  className = "",
}: TableToolbarProps) {
  const visibleFilters = (Array.isArray(filters) ? filters : []).filter(
    (f) => f && !f.hidden,
  );

  const showViewToggle = !!onViewModeChange;
  const showPageSize = !!onPageSizeChange;
  const hasRight = !!actions || showViewToggle || showPageSize;

  const submitSearch = (value: string) => search?.onSubmit?.(value.trim());

  const handleSearchChange = (value: string) => {
    search?.onChange(value);
    if (!value.trim()) submitSearch("");
  };

  return (
    <div
      className={`flex min-w-0 flex-1 flex-wrap items-center gap-3 ${className}`.trim()}
    >
      {search && (
        <div
          className={`relative w-full min-w-0 sm:w-auto sm:flex-1 ${search.className ?? "sm:max-w-md"}`.trim()}
        >
          <button
            type="button"
            aria-label={search.ariaLabel ?? "Search"}
            disabled={search.disabled}
            onClick={() => submitSearch(search.value)}
            className="absolute left-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#f1f5f9] hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white/50 dark:hover:bg-white/[0.05] dark:hover:text-indigo-400"
          >
            <Search className="size-4" />
          </button>
          <input
            type="text"
            value={search.value}
            disabled={search.disabled}
            aria-label={search.ariaLabel ?? search.placeholder ?? "Search"}
            placeholder={search.placeholder ?? "Search"}
            maxLength={search.maxLength ?? 200}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitSearch(search.value);
              }
            }}
            className="h-[38px] w-full rounded-lg border border-border bg-card pl-10 pr-9 text-sm text-foreground shadow-sm outline-none transition-all placeholder:text-[#64748b] hover:border-indigo-400/60 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:placeholder:text-white/50 dark:hover:border-indigo-400/40 dark:focus:border-indigo-500"
          />
          {!!search.value && !search.disabled && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => handleSearchChange("")}
              className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-[#64748b] transition-colors hover:bg-[#f1f5f9] hover:text-foreground dark:text-white/50 dark:hover:bg-white/[0.05]"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      )}

      {visibleFilters.length > 0 && (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {visibleFilters.map((filter, i) =>
            filter.multiple ? (
              <MultiSelectDropdown
                key={`${filter.key}-${i}`}
                size="sm"
                leadingLabel={filter.leadingLabel}
                selected={filter.values ?? []}
                onChange={filter.onValuesChange ?? (() => {})}
                options={filter.options ?? []}
                placeholder={filter.placeholder ?? "All"}
                selectAll={filter.selectAll ?? true}
                searchable={filter.searchable ?? ((filter.options?.length ?? 0) > 5)}
                disabled={filter.disabled}
                className={filter.className ?? ""}
              />
            ) : (
              <Dropdown
                key={`${filter.key}-${i}`}
                size="sm"
                variant="pill"
                value={filter.value ?? ""}
                onChange={filter.onChange ?? (() => {})}
                options={filter.options ?? []}
                placeholder={filter.placeholder}
                leadingLabel={filter.leadingLabel}
                searchable={filter.searchable}
                disabled={filter.disabled}
                className={filter.className ?? ""}
              />
            ),
          )}
          {filterTrailing}
        </div>
      )}

      {trailing}

      {hasRight && (
        <div className="flex w-full items-center justify-end gap-3 sm:ml-auto sm:w-auto sm:flex-shrink-0">
          {actions}
          {showViewToggle && !viewToggleLast && (
            <ViewModeToggle
              value={viewMode ?? "table"}
              onChange={onViewModeChange!}
            />
          )}
          {showPageSize && (
            <RowsPerCardSelect
              value={pageSize ?? DEFAULT_PAGE_SIZE}
              onChange={onPageSizeChange!}
              label={pageSizeLabel}
            />
          )}
          {showViewToggle && viewToggleLast && (
            <ViewModeToggle
              value={viewMode ?? "table"}
              onChange={onViewModeChange!}
            />
          )}
        </div>
      )}
    </div>
  );
}
